import React, { useEffect, useRef, useState, useMemo } from 'react';
import './NetworkGraph.css';

const NetworkGraph: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const animationFrameRef = useRef<number | null>(null);

  // Sample data - for a sleek neural network-like visualization as background
  // Memoize to prevent recreation on every render
  const graphData = useMemo(() => ({
    nodes: [
      // Companies - Good
      { id: 'comp1', type: 'company', status: 'good', size: 6, color: '#0abf53' },
      { id: 'comp2', type: 'company', status: 'good', size: 6, color: '#0abf53' },
      { id: 'comp3', type: 'company', status: 'good', size: 7, color: '#0abf53' },
      
      // Companies - Bad
      { id: 'comp4', type: 'company', status: 'bad', size: 7, color: '#5166e7' },
      { id: 'comp5', type: 'company', status: 'bad', size: 6, color: '#5166e7' },
      
      // Representatives - Good
      { id: 'rep1', type: 'node', status: 'good', size: 4, color: '#0abf53' },
      { id: 'rep2', type: 'node', status: 'good', size: 5, color: '#0abf53' },
      { id: 'rep3', type: 'node', status: 'good', size: 4, color: '#0abf53' },
      
      // Representatives - Bad
      { id: 'rep4', type: 'node', status: 'bad', size: 5, color: '#5166e7' },
      { id: 'rep5', type: 'node', status: 'bad', size: 4, color: '#5166e7' },
      { id: 'rep6', type: 'node', status: 'bad', size: 4, color: '#5166e7' },
      
      // Additional neural net nodes - increased for background effect
      { id: 'n1', type: 'node', status: 'neutral', size: 3, color: '#0abf53' },
      { id: 'n2', type: 'node', status: 'neutral', size: 3, color: '#0abf53' },
      { id: 'n3', type: 'node', status: 'neutral', size: 2.5, color: '#0abf53' },
      { id: 'n4', type: 'node', status: 'neutral', size: 2.5, color: '#5166e7' },
      { id: 'n5', type: 'node', status: 'neutral', size: 3, color: '#5166e7' },
      { id: 'n6', type: 'node', status: 'neutral', size: 3, color: '#0abf53' },
      { id: 'n7', type: 'node', status: 'neutral', size: 2.5, color: '#0abf53' },
      { id: 'n8', type: 'node', status: 'neutral', size: 3, color: '#5166e7' },
      { id: 'n9', type: 'node', status: 'neutral', size: 2.5, color: '#5166e7' },
      { id: 'n10', type: 'node', status: 'neutral', size: 2.5, color: '#0abf53' },
      { id: 'n11', type: 'node', status: 'neutral', size: 3, color: '#5166e7' },
      { id: 'n12', type: 'node', status: 'neutral', size: 2.5, color: '#0abf53' }
    ],
    links: [
      // Good connections
      { source: 'rep1', target: 'comp1', type: '', color: '#0abf53', width: 1 },
      { source: 'rep2', target: 'comp2', type: '', color: '#0abf53', width: 1 },
      { source: 'rep3', target: 'comp3', type: '', color: '#0abf53', width: 1 },
      { source: 'comp1', target: 'comp2', type: '', color: '#0abf53', width: 1 },
      { source: 'comp2', target: 'comp3', type: '', color: '#0abf53', width: 1 },
      
      // Bad connections
      { source: 'rep4', target: 'comp4', type: '', color: '#5166e7', width: 1 },
      { source: 'rep5', target: 'comp5', type: '', color: '#5166e7', width: 1 },
      { source: 'rep6', target: 'comp4', type: '', color: '#5166e7', width: 1 },
      { source: 'comp4', target: 'comp5', type: '', color: '#5166e7', width: 1 },
      
      // Cross connections
      { source: 'comp3', target: 'comp4', type: '', color: '#A9B0BC', width: 0.6 },
      { source: 'rep3', target: 'rep4', type: '', color: '#A9B0BC', width: 0.6 },
      
      // Neural network connections - expanded for background
      { source: 'n1', target: 'n2', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n2', target: 'n3', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n1', target: 'rep1', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n2', target: 'rep2', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n3', target: 'comp1', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n3', target: 'n4', type: '', color: '#a9b1c0', width: 0.5 },
      { source: 'n4', target: 'n5', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n4', target: 'comp3', type: '', color: '#a9b1c0', width: 0.5 },
      { source: 'n5', target: 'comp2', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n2', target: 'rep5', type: '', color: '#a9b1c0', width: 0.5 },
      { source: 'n3', target: 'comp4', type: '', color: '#a9b1c0', width: 0.5 },
      { source: 'n1', target: 'n5', type: '', color: '#a9b1c0', width: 0.5 },
      
      // Additional connections for background effect
      { source: 'n6', target: 'n7', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n7', target: 'n3', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n6', target: 'rep3', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n7', target: 'comp3', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n8', target: 'n9', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n9', target: 'n5', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n8', target: 'rep6', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n9', target: 'comp5', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n10', target: 'n12', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n11', target: 'n8', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n12', target: 'n1', type: '', color: '#0abf53', width: 0.5 },
      { source: 'n11', target: 'n9', type: '', color: '#5166e7', width: 0.5 },
      { source: 'n10', target: 'n6', type: '', color: '#0abf53', width: 0.5 }
    ]
  }), []); // Empty dependency array to memoize the data

  // Update dimensions when container size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const parentElement = containerRef.current.parentElement;
        if (parentElement) {
          const newWidth = parentElement.offsetWidth;
          const newHeight = parentElement.offsetHeight;
          
          // Only update if dimensions actually changed
          setDimensions(prev => {
            if (prev.width !== newWidth || prev.height !== newHeight) {
              return { width: newWidth, height: newHeight };
            }
            return prev;
          });
        } else {
          const newWidth = containerRef.current.offsetWidth;
          const newHeight = containerRef.current.offsetHeight;
          
          // Only update if dimensions actually changed
          setDimensions(prev => {
            if (prev.width !== newWidth || prev.height !== newHeight) {
              return { width: newWidth, height: newHeight };
            }
            return prev;
          });
        }
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    // Use ResizeObserver for more efficient dimension tracking
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Add visibility detection to optimize performance
  useEffect(() => {
    let intersectionObserver: IntersectionObserver | null = null;
    
    if (containerRef.current && window.IntersectionObserver) {
      intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        { threshold: 0.1 }
      );
      intersectionObserver.observe(containerRef.current);
    }
    
    return () => {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
    };
  }, []);

  // Create a futuristic neural network-like visualization
  useEffect(() => {
    if (!containerRef.current || dimensions.width === 0 || !isVisible) return;

    const canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    canvas.style.borderRadius = '8px';
    
    // Clear any existing canvas
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }
    
    containerRef.current.appendChild(canvas);
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set up the animation
    let time = 0;
    
    // Create a transparent backdrop for background use
    ctx.fillStyle = 'rgba(8, 12, 24, 0)'; // Fully transparent
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Skip grid effect for background use
    
    // Position nodes in a spread-out network layout
    const nodePositions: Record<string, {x: number, y: number}> = {};
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(canvas.width, canvas.height) * 0.9;  // Increased radius to fill more space
    
    graphData.nodes.forEach((node, i) => {
      // Create a more dynamic layout
      let angle, r;
      
      if (node.type === 'company') {
        // Companies are positioned in an outer ring
        angle = (i % 5 / 5) * Math.PI * 2;
        r = radius * (0.8 + Math.random() * 0.2);
      } else {
        // Nodes are distributed across the visualization
        angle = (i / graphData.nodes.length) * Math.PI * 2;
        r = radius * (0.3 + Math.random() * 0.5);
      }
      
      nodePositions[node.id] = {
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle)
      };
    });
    
    const render = () => {
      time += 0.001;  // Much slower animation for better performance
      
      // Clear canvas with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // For background use we skip the grid and keep it transparent
      
      // Draw connections
      graphData.links.forEach(link => {
        const sourcePos = nodePositions[link.source as string];
        const targetPos = nodePositions[link.target as string];
        
        if (sourcePos && targetPos) {
          // Calculate distance for glow effect
          const dx = targetPos.x - sourcePos.x;
          const dy = targetPos.y - sourcePos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Determine connection opacity
          const pulseIntensity = 0.5 + 0.5 * Math.sin(time * 3 + parseInt(link.source as string, 36) % 10);
          const opacity = 0.3 + 0.2 * pulseIntensity;
          
          // Draw line with subtle glow effect - more transparent for background use
          ctx.beginPath();
          ctx.moveTo(sourcePos.x, sourcePos.y);
          ctx.lineTo(targetPos.x, targetPos.y);
          
          // Create gradient for the connection - more transparent for background
          const gradient = ctx.createLinearGradient(
            sourcePos.x, sourcePos.y, targetPos.x, targetPos.y
          );
          
          // Make connections more transparent for background use
          const baseColor = link.color.startsWith('#') 
            ? link.color 
            : link.color.replace(')', ', 0.6)').replace('rgb', 'rgba');
            
          gradient.addColorStop(0, baseColor.replace(')', ', 0.4)').replace('rgb', 'rgba'));
          gradient.addColorStop(1, baseColor.replace(')', ', ' + (opacity * 0.3) + ')').replace('rgb', 'rgba'));
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = (link.width || 0.8) * 0.8; // Thinner lines for background
          ctx.stroke();
          
          // Add very subtle glow effect
          ctx.shadowColor = baseColor;
          ctx.shadowBlur = 2;
          ctx.stroke();
          ctx.shadowBlur = 0;
          
          // Animated data pulses - very subtle and minimal for background
          const particleCount = Math.max(1, Math.floor(distance / 120));
          for (let i = 0; i < particleCount; i++) {
            const particleOffset = ((time * 0.6 + i / particleCount) % 1);
            const particleX = sourcePos.x + (targetPos.x - sourcePos.x) * particleOffset;
            const particleY = sourcePos.y + (targetPos.y - sourcePos.y) * particleOffset;
            
            const pulseSize = Math.sin(Math.PI * particleOffset) * 1.5 * (link.width || 0.6);
            
            // Only draw if pulse has a reasonable size
            if (pulseSize > 0.2) {
              ctx.beginPath();
              ctx.arc(particleX, particleY, pulseSize, 0, Math.PI * 2);
              
              // More transparent for background
              const pulseColor = link.color.startsWith('#')
                ? link.color
                : link.color.replace(')', ', 0.3)').replace('rgb', 'rgba');
              
              ctx.fillStyle = pulseColor.replace(')', ', 0.3)').replace('rgb', 'rgba');
              ctx.fill();
              
              // Very subtle glow for background
              ctx.shadowColor = pulseColor;
              ctx.shadowBlur = 2;
              ctx.fill();
              ctx.shadowBlur = 0;
            }
          }
        }
      });
      
      // Draw nodes with a sleek, modern style
      graphData.nodes.forEach(node => {
        const pos = nodePositions[node.id];
        
        if (pos) {
          // Apply a very subtle float animation
          const floatY = Math.sin(time * 1.5 + parseInt(node.id.replace(/[^0-9]/g, '1'), 10) * 0.5) * 2;
          const pulseSize = 1 + 0.03 * Math.sin(time * 2 + parseInt(node.id, 36) % 10);
          
          // Draw node glow - even more subtle for background
          ctx.beginPath();
          ctx.arc(pos.x, pos.y + floatY, node.size * 1.3 * pulseSize, 0, Math.PI * 2);
          const gradientGlow = ctx.createRadialGradient(
            pos.x, pos.y + floatY, 0,
            pos.x, pos.y + floatY, node.size * 1.8 * pulseSize
          );
          gradientGlow.addColorStop(0, node.color.replace(')', ', 0.15)').replace('rgb', 'rgba'));
          gradientGlow.addColorStop(1, node.color.replace(')', ', 0)').replace('rgb', 'rgba'));
          ctx.fillStyle = gradientGlow;
          ctx.fill();
          
          // Draw node circle - sleeker style
          ctx.beginPath();
          ctx.arc(pos.x, pos.y + floatY, node.size * pulseSize, 0, Math.PI * 2);
          
          let gradientNode;
          if (node.status === 'good') {
            // Create a sleek gradient for good nodes - more transparent for background
            gradientNode = ctx.createRadialGradient(
              pos.x - node.size * 0.3, pos.y + floatY - node.size * 0.3, 0,
              pos.x, pos.y + floatY, node.size * pulseSize
            );
            gradientNode.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
            gradientNode.addColorStop(0.3, 'rgba(111, 249, 222, 0.6)');
            gradientNode.addColorStop(1, 'rgba(54, 241, 205, 0.4)');
          } else if (node.status === 'bad') {
            // Create a sleek gradient for bad nodes - more transparent for background
            gradientNode = ctx.createRadialGradient(
              pos.x - node.size * 0.3, pos.y + floatY - node.size * 0.3, 0,
              pos.x, pos.y + floatY, node.size * pulseSize
            );
            gradientNode.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
            gradientNode.addColorStop(0.3, 'rgba(255, 99, 132, 0.6)');
            gradientNode.addColorStop(1, 'rgba(255, 58, 94, 0.4)');
          } else {
            // Neutral nodes take on a subtle gradient - more transparent for background
            gradientNode = ctx.createRadialGradient(
              pos.x - node.size * 0.3, pos.y + floatY - node.size * 0.3, 0,
              pos.x, pos.y + floatY, node.size * pulseSize
            );
            gradientNode.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
            gradientNode.addColorStop(0.5, node.color.replace(')', ', 0.5)').replace('rgb', 'rgba'));
            gradientNode.addColorStop(1, node.color.replace(')', ', 0.3)').replace('rgb', 'rgba'));
          }
          
          ctx.fillStyle = gradientNode;
          ctx.fill();
          
          // Even subtler white border for background
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.lineWidth = 0.3;
          ctx.stroke();
          
          // More subtle energy rings for background
          if (node.size > 5) {
            const ringScale = (Math.sin(time * 1.2 + parseInt(node.id, 36) % 10) + 1) * 0.5;
            const ringRadius = node.size * (1.8 + ringScale * 1.5);
            
            ctx.beginPath();
            ctx.arc(pos.x, pos.y + floatY, ringRadius, 0, Math.PI * 2);
            ctx.strokeStyle = node.color.replace(')', ', ' + (0.08 - ringScale * 0.05) + ')').replace('rgb', 'rgba');
            ctx.lineWidth = 0.3;
            ctx.stroke();
          }
        }
      });
      
      animationFrameRef.current = requestAnimationFrame(render);
    };
    
    render();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [dimensions, graphData, isVisible]);

  return (
    <div className="graph-container" ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {/* Canvas will be added here by the effect */}
    </div>
  );
};

export default NetworkGraph;
